<script setup lang="ts">
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import IconAdd from '@patsnap-ui/icon/assets/solid/AddMedium.svg';
import IconArrowRight from '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import IconDelete from '@patsnap-ui/icon/assets/solid/delete.svg';
import EmbodimentFeatureSelector from './EmbodimentFeatureSelector.vue';
import EmbodimentKeywordSelector from './EmbodimentKeywordSelector.vue';
import { computed } from 'vue';
import { useEmbodimentRules } from './composables/use-embodiment-rules';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const {
    embodimentRules,
    createNewRule,
    deleteRule,
    updateFeature,
    updateKeyword,
} = useEmbodimentRules();

const canDelete = computed(() => {
    return embodimentRules.value.length > 1;
});
</script>

<template>
    <div class="embodiment-config rounded-lg border border-gray-40 w-full h-full overflow-hidden flex flex-col">
        <!-- 配置头部 - 包含列标题 -->
        <div class="embodiment-config_header bg-gray-20 grid grid-cols-[64px_1fr_56px_1fr_56px] gap-0 py-1 flex-shrink-0">
            <div />
            <!-- 替换特征列标题 -->
            <div>{{ t('aiSpecification.embodiment.replacedFeature') }}</div>
            <!-- 箭头图标列 -->
            <div class="justify-center">
                <div class="h-4 w-4 rounded-lg bg-green-200 overflow-hidden">
                    <SvgIcon
                        :icon="IconArrowRight"
                        class="h-4 w-4 fill-white relative left-[1px]"
                    />
                </div>
            </div>
            <!-- 替换词列标题 -->
            <div>{{ t('aiSpecification.embodiment.replacementTerm') }}</div>
            <div />
        </div>

        <!-- 配置内容区域 - 包含规则列表 -->
        <div class="embodiment-config_content overflow-auto flex-1">
            <!-- 单条规则项 -->
            <div
                v-for="(item, index) in embodimentRules"
                :key="item.id"
                class="embodiment-config_content-item grid grid-cols-[64px_1fr_56px_1fr_56px] gap-0 py-2 border-t border-gray-40 hover:bg-gray-20 group"
            >
                <!-- 规则序号 -->
                <div>{{ t('aiSpecification.embodiment.rule', { num: index + 1 }) }}</div>
                <!-- 特征选择器 -->
                <div>
                    <EmbodimentFeatureSelector
                        :modelValue="item.claimFeature"
                        @update:modelValue="updateFeature(item.id, $event)"
                    />
                </div>
                <!-- 箭头图标 -->
                <div class="justify-center">
                    <div class="h-4 w-4 rounded-lg bg-green-200 overflow-hidden">
                        <SvgIcon
                            :icon="IconArrowRight"
                            class="h-4 w-4 fill-white relative left-[1px]"
                        />
                    </div>
                </div>
                <!-- 关键词选择器 -->
                <div>
                    <EmbodimentKeywordSelector
                        :key="item.claimFeature.feature"
                        :modelValue="item.keyword"
                        :claimFeature="item.claimFeature"
                        :disabled="!item.claimFeature.feature"
                        @update:modelValue="updateKeyword(item.id, $event)"
                    />
                </div>
                <!-- 删除按钮 - 仅在hover且可删除时显示 -->
                <div
                    :class="{ 'group-hover:!flex': canDelete }"
                    class="!hidden"
                >
                    <SvgIcon
                        :icon="IconDelete"
                        class="h-6 w-6 fill-gray-450 cursor-pointer"
                        @click="deleteRule(item.id)"
                    />
                </div>
            </div>
        </div>

        <!-- 配置底部 - 添加规则按钮 -->
        <div class="embodiment-config_footer flex justify-center py-1 border-t border-gray-40 flex-shrink-0">
            <button
                class="th-btn th-btn-plain th-btn-sm"
                @click="createNewRule"
            >
                <SvgIcon
                    class="th-btn-icon h-6 w-6 fill-gray-450"
                    :icon="IconAdd"
                />
                <span>{{ t('aiSpecification.embodiment.addRule') }}</span>
            </button>
        </div>
    </div>
</template>

<style lang="scss">
.embodiment-config {
    .embodiment-config_header > div,
    .embodiment-config_content-item > div {
        @apply text-gray-90 px-2 py-1 leading-6 flex items-center overflow-hidden;
    }
}
</style>
